import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { FieldButtonGroup } from "@/components/forms/FieldButtonGroup.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import { useAriaField, type AriaFieldOptions } from "@/components/forms/ariaFieldRegister.ts";
import { cn } from "@/utils/classNames.ts";
import { useLingui } from "@lingui/react/macro";
import type { VmParameter } from "@warrenio/api-spec/spec.oats.gen";
import { useMemo, useState, type ReactNode } from "react";
import { Input, TextArea } from "react-aria-components";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

export function ParamsTextField<TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues>>({
    field,
    form,
    params,
    children,
    registerOptions,
    multilineRows,
    type = "text",
    isMonospace = false,
    ...textFieldProps
}: Omit<WTextFieldProps, "form"> & {
    field: TFieldName;
    params: VmParameter;
    children?: ReactNode;
    form: UseFormReturn<TFieldValues>;
    registerOptions?: NoInfer<AriaFieldOptions<TFieldValues, TFieldName>>;
    multilineRows?: number;
    isMonospace?: boolean;
}) {
    //#region Hooks
    const [passwordVisible, setPasswordVisible] = useState(false);

    // NB: Since "native" and RHF validation do not mix, keep all validation fully custom

    const validate = useParamsValidate(params);

    const { ref, props } = useAriaField<TFieldValues, TFieldName>(field, form, {
        isRequired: params.mandatory,
        validate,
        ...registerOptions,
    });

    return (
        <WTextField
            // NB: Discard description since it's in English and not localized properly
            // description={params.description}
            type={type}
            {...props}
            {...textFieldProps}
        >
            {multilineRows !== undefined ? (
                <TextArea
                    rows={multilineRows}
                    ref={ref}
                    className={cn(isMonospace && "font-mono", FF.FormFieldTextArea, TF.TextArea)}
                />
            ) : type === "password" ? (
                <FieldButtonGroup hasAbsoluteButton>
                    <Input
                        type={passwordVisible ? "text" : "password"}
                        ref={ref}
                        className={cn(FF.FormFieldInput, TF.Input)}
                    />

                    <WButton
                        size="xs"
                        variant="ghost"
                        action={() => setPasswordVisible((v) => !v)}
                        data-testid="ShowHideButton"
                        icon={cn("size-0.875rem", passwordVisible ? "jp-nopreview-icon" : "jp-preview-icon")}
                    />
                </FieldButtonGroup>
            ) : (
                <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
            )}

            {children}
        </WTextField>
    );
}

function useParamsValidate({ constraint, expression }: VmParameter) {
    const { t } = useLingui();
    const regExp = useMemo(() => {
        return constraint === "regexp" && expression ? new RegExp(expression, "u") : undefined;
    }, [constraint, expression]);

    return (value: string) =>
        // NB: empty value is handled by "required" validator elsewhere
        value && regExp?.test(value) === false ? t`Value does not match required pattern` : undefined;
}
